import React, { useState, useEffect, useCallback } from "react";
import { GitBranch, Plus, Edit, Trash2, Save, X, Building2 } from "lucide-react";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const errMsg = async (res, fb) => {
  try { const e = await res.json(); if (typeof e.detail === "string") return e.detail; } catch (_) { /* sin cuerpo */ }
  return `${fb} (${res.status})`;
};

// Grupos de empresas: una empresa padre + sucursales, con módulos compartidos (RRHH).
const SucursalesPanel = ({ token, empresas = [] }) => {
  const [grupos, setGrupos] = useState([]);
  const [compartibles, setCompartibles] = useState([]);
  const [form, setForm] = useState(null); // {id?, nombre, padre_id, sucursales_ids, modulos_compartidos}
  const [saving, setSaving] = useState(false);
  const headers = { Authorization: `Bearer ${token}`, "Content-Type": "application/json" };

  const cargar = useCallback(async () => {
    try {
      const [g, m] = await Promise.all([
        fetch(`${API}/admin/grupos-empresas`, { headers }),
        fetch(`${API}/admin/grupos-empresas/modulos-compartibles`, { headers }),
      ]);
      if (g.ok) setGrupos(await g.json());
      if (m.ok) setCompartibles(await m.json());
    } catch (_) { toast.error("No se pudieron cargar los grupos"); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  useEffect(() => { cargar(); }, [cargar]);

  const enOtroGrupo = (empId) =>
    grupos.some((g) => g.id !== form?.id && (g.empresas || []).some((e) => e.id === empId));

  const guardar = async () => {
    if (!form.nombre.trim()) return toast.error("Poné un nombre al grupo");
    if (!form.padre_id) return toast.error("Elegí la empresa padre");
    if (!form.sucursales_ids.length) return toast.error("Elegí al menos una sucursal");
    setSaving(true);
    try {
      const body = JSON.stringify({
        nombre: form.nombre, padre_id: form.padre_id,
        sucursales_ids: form.sucursales_ids, modulos_compartidos: form.modulos_compartidos,
      });
      const res = await fetch(`${API}/admin/grupos-empresas${form.id ? `/${form.id}` : ""}`, {
        method: form.id ? "PUT" : "POST", headers, body,
      });
      if (!res.ok) return toast.error(await errMsg(res, "No se pudo guardar"));
      toast.success("Grupo guardado");
      setForm(null);
      cargar();
    } finally { setSaving(false); }
  };

  const eliminar = async (g) => {
    if (!window.confirm(`¿Deshacer el grupo «${g.nombre}»? Las empresas quedan independientes y cada una conserva sus datos.`)) return;
    const res = await fetch(`${API}/admin/grupos-empresas/${g.id}`, { method: "DELETE", headers });
    if (!res.ok) return toast.error(await errMsg(res, "No se pudo eliminar"));
    toast.success("Grupo eliminado");
    cargar();
  };

  const editar = (g) => setForm({
    id: g.id, nombre: g.nombre, padre_id: g.padre_id,
    sucursales_ids: (g.empresas || []).map((e) => e.id).filter((i) => i !== g.padre_id),
    modulos_compartidos: g.modulos_compartidos || [],
  });

  const toggle = (campo, val) => setForm((f) => ({
    ...f, [campo]: f[campo].includes(val) ? f[campo].filter((x) => x !== val) : [...f[campo], val],
  }));

  return (
    <div className="bg-arandu-dark-light border border-white/5 rounded-xl p-5 mb-6" data-testid="sucursales-panel">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <GitBranch className="w-5 h-5 text-arandu-blue" />
          <h2 className="font-heading text-lg font-bold text-white">Sucursales</h2>
        </div>
        {!form && (
          <Button size="sm" className="bg-arandu-blue hover:bg-arandu-blue-dark text-white"
            onClick={() => setForm({ nombre: "", padre_id: "", sucursales_ids: [], modulos_compartidos: [] })}>
            <Plus className="w-4 h-4 mr-1" /> Nuevo grupo
          </Button>
        )}
      </div>
      <p className="text-slate-400 text-xs mb-4">
        Uní 2 o más empresas: elegí la empresa padre y sus sucursales, y qué módulos manejan en conjunto (por ahora RRHH).
      </p>

      {form && (
        <div className="border border-arandu-blue/30 rounded-lg p-4 mb-4 space-y-3 bg-black/20">
          <Input value={form.nombre} onChange={(e) => setForm({ ...form, nombre: e.target.value })}
            placeholder="Nombre del grupo" className="bg-arandu-dark border-white/10 text-white" />
          <div>
            <label className="text-xs text-slate-400">Empresa padre</label>
            <select value={form.padre_id}
              onChange={(e) => setForm({ ...form, padre_id: e.target.value, sucursales_ids: form.sucursales_ids.filter((i) => i !== e.target.value) })}
              className="w-full mt-1 bg-arandu-dark border border-white/10 rounded-md px-3 py-2 text-white text-sm">
              <option value="">Seleccionar…</option>
              {empresas.filter((e) => !enOtroGrupo(e.id)).map((e) => <option key={e.id} value={e.id}>{e.nombre}</option>)}
            </select>
          </div>
          <div>
            <label className="text-xs text-slate-400">Sucursales</label>
            <div className="flex flex-wrap gap-2 mt-1">
              {empresas.filter((e) => e.id !== form.padre_id && !enOtroGrupo(e.id)).map((e) => (
                <label key={e.id} className={`px-3 py-1.5 rounded-lg border text-sm cursor-pointer ${form.sucursales_ids.includes(e.id) ? "border-arandu-blue bg-arandu-blue/20 text-white" : "border-white/10 text-slate-300"}`}>
                  <input type="checkbox" className="mr-2" checked={form.sucursales_ids.includes(e.id)} onChange={() => toggle("sucursales_ids", e.id)} />
                  {e.nombre}
                </label>
              ))}
            </div>
          </div>
          <div>
            <label className="text-xs text-slate-400">Módulos que manejan en conjunto</label>
            <div className="flex flex-wrap gap-3 mt-1">
              {compartibles.map((m) => (
                <label key={m.key} className="text-sm text-slate-200 flex items-center gap-2">
                  <input type="checkbox" checked={form.modulos_compartidos.includes(m.key)} onChange={() => toggle("modulos_compartidos", m.key)} />
                  {m.label}
                </label>
              ))}
            </div>
            <p className="text-[11px] text-slate-500 mt-1">
              Con RRHH compartido, los empleados se ven desde todas las empresas del grupo y en cada empleado elegís a qué empresa pertenece (ahí cae lo contable y el sueldo).
            </p>
          </div>
          <div className="flex gap-2 justify-end">
            <Button variant="ghost" onClick={() => setForm(null)} className="text-slate-300"><X className="w-4 h-4 mr-1" />Cancelar</Button>
            <Button onClick={guardar} disabled={saving} className="bg-arandu-blue hover:bg-arandu-blue-dark text-white"><Save className="w-4 h-4 mr-1" />Guardar</Button>
          </div>
        </div>
      )}

      {grupos.length === 0 && !form && <p className="text-slate-500 text-sm">Todavía no hay grupos.</p>}
      <div className="space-y-2">
        {grupos.map((g) => (
          <div key={g.id} className="flex items-center justify-between gap-3 border border-white/5 rounded-lg px-3 py-2">
            <div className="min-w-0">
              <p className="text-white font-medium text-sm">{g.nombre}</p>
              <p className="text-xs text-slate-400 flex flex-wrap gap-x-3">
                {(g.empresas || []).map((e) => (
                  <span key={e.id} className="inline-flex items-center gap-1">
                    <Building2 className="w-3 h-3" />{e.nombre}{e.id === g.padre_id ? " (padre)" : ""}
                  </span>
                ))}
              </p>
              {(g.modulos_compartidos || []).length > 0 && (
                <p className="text-[11px] text-emerald-400 mt-0.5">Compartido: {g.modulos_compartidos.join(", ").toUpperCase()}</p>
              )}
            </div>
            <div className="flex gap-1 shrink-0">
              <Button size="icon" variant="ghost" onClick={() => editar(g)} className="text-slate-300"><Edit className="w-4 h-4" /></Button>
              <Button size="icon" variant="ghost" onClick={() => eliminar(g)} className="text-red-400"><Trash2 className="w-4 h-4" /></Button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default SucursalesPanel;
